import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";

// REDUX
import {
  dispatchOrders,
  selectAllOrders,
} from "../../store/actions/ordersSlice";

// MUI Components
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import DirectionsIcon from "@mui/icons-material/Directions";
import InventoryIcon from "@mui/icons-material/Inventory";

// Animated Modal Components
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  hover: {
    backgroundColor: "#f5f5f5",
    scale: 1.01,
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

export default function SetOrderToDriverModal({ closeModal }) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const orders = useSelector(selectAllOrders);
  const driver = useSelector((state) => state.drivers.selectedDriver);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectValue, setSelectValue] = useState(null);
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const availableOrders = orders
      .filter((order) => order.status_number === 2 || order.status_number === 1)
      .map((order) => ({
        value: order.order_id,
        label: order.channel_code,
        address: `${order.street}, ${order.number}, ${order.neighborhood}`,
        orderData: order,
        status: order.status_number,
        hasCard: order.has_card,
      }));

    setOptions(availableOrders);
    setFilteredOptions(availableOrders);
  }, [orders]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions(options);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = options.filter(
        (option) =>
          option.label.toLowerCase().includes(lowercasedSearch) ||
          option.address.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectOrder = (order) => {
    setSelectValue(order);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!selectValue) {
      setError("Selecione um pedido antes de continuar.");
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        dispatchOrders({
          orderId: selectValue.value,
          driverId: driver.driver_id,
        })
      ).unwrap();

      closeModal();
    } catch (e) {
      console.error(e);
      setError("Ocorreu um erro ao despachar o pedido. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return theme.palette.info.main;
      case 2:
        return theme.palette.success.main;
      default:
        return theme.palette.text.primary;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "Pendente";
      case 2:
        return "Pronto";
      default:
        return "Desconhecido";
    }
  };

  return (
    <AnimatedModal
      isOpen={true}
      onClose={closeModal}
      width="500px"
      maxWidth="95%"
      zIndex={10000}
      forceOnTop={true}
    >
      <ModalHeader
        title="Atribuir Pedido ao Entregador"
        icon={LocalShippingIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <AnimatePresence>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Driver Info Section */}
            <motion.div variants={itemVariants}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: theme.palette.primary.main,
                    }}
                  >
                    {driver?.name[0] || "D"}
                  </Avatar>

                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {driver?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {driver?.easy_id || "N/A"}
                      {driver?.phone && ` • Tel: ${driver.phone}`}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>

            {/* Order Selection Section */}
            <motion.div variants={itemVariants}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <ShoppingBasketIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Selecione o Pedido
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Buscar pedido por código ou endereço..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    pr: 1,
                    borderRadius: 1,
                  }}
                >
                  {filteredOptions.length === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        borderRadius: 1,
                        backgroundColor: alpha(
                          theme.palette.background.default,
                          0.5
                        ),
                      }}
                    >
                      <InventoryIcon
                        sx={{
                          fontSize: 48,
                          color: alpha(theme.palette.text.secondary, 0.5),
                          mb: 1,
                        }}
                      />
                      <Typography color="text.secondary">
                        {searchTerm
                          ? `Nenhum pedido encontrado para "${searchTerm}"`
                          : "Nenhum pedido disponível para despacho"}
                      </Typography>
                    </Box>
                  ) : (
                    filteredOptions.map((order) => (
                      <motion.div
                        key={order.value}
                        variants={listItemVariants}
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <Paper
                          elevation={0}
                          onClick={() => handleSelectOrder(order)}
                          sx={{
                            p: 2,
                            mb: 1.5,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            border: `1px solid ${
                              selectValue?.value === order.value
                                ? theme.palette.primary.main
                                : alpha(theme.palette.divider, 0.3)
                            }`,
                            backgroundColor:
                              selectValue?.value === order.value
                                ? alpha(theme.palette.primary.light, 0.05)
                                : "transparent",
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  color={
                                    selectValue?.value === order.value
                                      ? theme.palette.primary.main
                                      : "text.primary"
                                  }
                                >
                                  {order.label}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getStatusText(order.status)}
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    bgcolor: alpha(
                                      getStatusColor(order.status),
                                      0.1
                                    ),
                                    color: getStatusColor(order.status),
                                  }}
                                />
                                {order.hasCard && (
                                  <Tooltip title="Pagamento com cartão">
                                    <Chip
                                      size="small"
                                      label="Cartão"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.7rem",
                                        bgcolor: alpha(
                                          theme.palette.warning.main,
                                          0.1
                                        ),
                                        color: theme.palette.warning.main,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>

                              {selectValue?.value === order.value && (
                                <CheckCircleIcon
                                  fontSize="small"
                                  color="primary"
                                />
                              )}
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <MyLocationIcon
                                fontSize="small"
                                sx={{
                                  color: alpha(
                                    theme.palette.text.secondary,
                                    0.7
                                  ),
                                  fontSize: 14,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.75rem",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {order.address}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Paper>
                      </motion.div>
                    ))
                  )}
                </Box>
              </Paper>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </ModalContent>

      <ModalFooter>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={closeModal}
                sx={{ height: 46 }}
              >
                Cancelar
              </Button>
            </motion.div>
          </Grid>
          <Grid item xs={6}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleFormSubmit}
                disabled={!selectValue || isSubmitting}
                startIcon={isSubmitting ? null : <DirectionsIcon />}
                sx={{ height: 46 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Despachar"
                )}
              </Button>
            </motion.div>
          </Grid>
        </Grid>
      </ModalFooter>
    </AnimatedModal>
  );
}
